import { useCallback, useState } from 'react';
import { v4 as uuid } from 'uuid';
import Notification from 'components/ui/base/notification/Notification';
import useDeclarations from 'hooks/useDeclarations';
import {
    saveIcs2ReferralDraft,
    submitIcs2ReferralResponse,
    validateIcs2ReferralResponse,
} from 'store/declarations/eu/ics2/customs-requests/client';
import { describeReferralError } from 'store/declarations/eu/ics2/customs-requests/errors';
import { Ics2CustomsRequest, Ics2Referral, Ics2ReferralDraft } from 'store/declarations/eu/ics2/customs-requests/types';
import { getResponseMessageType } from 'store/declarations/eu/ics2/customs-requests/utils';
import { Ics2ReferralRow, getDocumentVersions } from '../referralDocuments';
import { SendContext, draftVersionsOf, findSendBlocker } from '../referralSending';

type Args = {
    declarationId?: string;
    rows: Ics2ReferralRow[];
    onDraftSaved: (request: Ics2CustomsRequest, saved: Ics2Referral) => void;
    onSent: () => Promise<unknown>;
};

const notifyError = (messageTitle: string, description: string) =>
    Notification({ type: 'error', messageTitle, description, placement: 'top' });

const useIcs2ReferralSubmission = ({ declarationId, rows, onDraftSaved, onSent }: Args) => {
    const { getDeclaration } = useDeclarations();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const saveDraft = useCallback(
        async (request: Ics2CustomsRequest, referral: Ics2Referral, data: Ics2ReferralDraft) => {
            if (!declarationId) return undefined;
            try {
                const saved = await saveIcs2ReferralDraft(
                    declarationId,
                    request.notificationId,
                    referral.id,
                    referral.draftVersion ?? 0,
                    data
                );
                onDraftSaved(request, saved);
                return saved;
            } catch (error) {
                notifyError('Could not save the draft', describeReferralError(error, 'Please review the answer.'));
                return undefined;
            }
        },
        [declarationId, onDraftSaved]
    );

    const send = useCallback(
        async (selected: Ics2ReferralRow[], context: SendContext): Promise<boolean> => {
            if (!declarationId || !selected.length) return false;

            const blocker = findSendBlocker(selected, context);
            if (blocker) {
                notifyError(blocker.title, blocker.description);
                return false;
            }

            setIsSubmitting(true);
            try {
                const versions = {
                    draftVersions: draftVersionsOf(selected),
                    documentVersions: getDocumentVersions(context.documents, selected),
                };
                await validateIcs2ReferralResponse(declarationId, versions);
                await submitIcs2ReferralResponse(declarationId, uuid(), versions);

                Notification({
                    type: 'success',
                    messageTitle: 'Response queued',
                    // Queued by our dispatcher - Customs never acknowledges a referral response.
                    description: `One ${getResponseMessageType(selected[0].request)} covering ${
                        selected.length
                    } referral${selected.length === 1 ? '' : 's'} is being sent to Customs.`,
                    placement: 'top',
                });

                await Promise.all([onSent(), getDeclaration(declarationId)]);
                return true;
            } catch (error) {
                notifyError('Could not send the response', describeReferralError(error, 'Please review the response.'));
                return false;
            } finally {
                setIsSubmitting(false);
            }
        },
        [declarationId, onSent, getDeclaration]
    );

    const saveAndSubmit = useCallback(
        async (request: Ics2CustomsRequest, referral: Ics2Referral, data: Ics2ReferralDraft, context: SendContext) => {
            const saved = await saveDraft(request, referral, data);
            if (!saved) return false;
            return send([{ request, referral: { ...referral, ...saved } }], context);
        },
        [saveDraft, send]
    );

    /** Answers the given referrals with a single response message. */
    const submitSelection = useCallback(
        async (referralIds: string[], context: SendContext) => {
            const selected = rows.filter((row) => referralIds.includes(row.referral.id));
            if (selected.length !== referralIds.length) return false;
            return send(selected, context);
        },
        [rows, send]
    );

    return { isSubmitting, saveDraft, saveAndSubmit, submitSelection };
};

export default useIcs2ReferralSubmission;
